// WorkspacesSection.jsx
// Manages the Interactive Workspaces table, all workspaces state/logic,
// and all menus/drawers/dialogs specific to workspaces.
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Typography, Alert, Box, Button, Menu, MenuItem } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import DeleteIcon from "@mui/icons-material/Delete";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";

import {
  supabaseAuthenticatedApi,
  workspaceBackendUrl,
} from "../../../../components/SupabaseClient";
import { SideDrawer } from "../../../../components/SideDrawer";

import usePolling from "../../../../hooks/usePolling";

import WorkspacesListTable from "./WorkspacesListTable";
import SectionHeader from "../components/SectionHeader";
import ConditionsTable from "../components/ConditionsTable";
import ExperimentProfile from "../components/ExperimentProfile";
import { mapStatusRequests } from "../components/mapStatusRequests";
import {
  formatTimestamp,
  StyledListItemIcon,
  ProgressMessage,
  PodLogsView,
  ConfirmTerminateDialog,
  ACTION_MENU_PROPS,
} from "../components/ViewUtils";

// Only used inside this section — not shared with jobs
const StatefulSetConditionDetailsView = ({ statefulset, pod }) => (
  <Box sx={{ p: 1, overflow: "auto" }}>
    <Typography variant="h6" gutterBottom>
      StatefulSet Conditions History
    </Typography>
    <ConditionsTable
      conditions={statefulset?.conditions || []}
      formatTimestamp={formatTimestamp}
    />
    <Box mt={4}>
      <Typography variant="h6" gutterBottom>
        Pod Conditions History
      </Typography>
      <ConditionsTable
        conditions={pod?.conditions || []}
        formatTimestamp={formatTimestamp}
      />
    </Box>
  </Box>
);
StatefulSetConditionDetailsView.propTypes = {
  statefulset: PropTypes.object,
  pod: PropTypes.object,
};

const WorkspacesSection = ({ onWorkspaceCreation }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(true);
  const [workspacesError, setWorkspacesError] = useState(null);

  const [expandedRows, setExpandedRows] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState(null);

  const [workspaceTerminating, setWorkspaceTerminating] = useState(false);
  const [workspaceTerminationError, setWorkspaceTerminationError] =
    useState(null);

  const [workspaceMenuState, setWorkspaceMenuState] = useState({
    anchorEl: null,
    workspaceId: null,
  });
  const [podMenuState, setPodMenuState] = useState({
    anchorEl: null,
    pod: null,
  });

  const [profileDrawerState, setProfileDrawerState] = useState({
    open: false,
    workspace: null,
  });
  const [logDrawerState, setLogDrawerState] = useState({
    open: false,
    pod: null,
  });
  const [logDrawerError, setLogDrawerError] = useState(null);
  const [logDrawerContent, setLogDrawerContent] = useState("");
  const [logDrawerLoading, setLogDrawerLoading] = useState(false);

  const [detailsDrawerState, setDetailsDrawerState] = useState({
    open: false,
    statefulset: null,
    pod: null,
  });

  useEffect(() => {
    if (logDrawerState.open && logDrawerState.pod) {
      fetchPodLogs(
        logDrawerState.pod.workspace_id,
        logDrawerState.pod.node_index,
      );
      const id = setInterval(() => {
        fetchPodLogs(
          logDrawerState.pod.workspace_id,
          logDrawerState.pod.node_index,
        );
      }, 5000);
      return () => clearInterval(id);
    }
  }, [logDrawerState.open, logDrawerState.pod]);

  const fetchWorkspaces = async (isActive) => {
    setWorkspacesError(null);
    try {
      const workspaceListResp = await supabaseAuthenticatedApi.get(
        "/workspace/list",
        workspaceBackendUrl,
      );
      const workspaceList = await workspaceListResp.json();
      if (!isActive()) return;
      if (!workspaceList.workspaces || workspaceList.count === 0) {
        setWorkspaces([]);
      } else {
        const workspacesStatus = await mapStatusRequests(
          workspaceList.workspaces,
          async (wsInfo) => {
            try {
              const url = `/workspace/status/${wsInfo.workspace_id}`;
              const resp = await supabaseAuthenticatedApi.get(
                url,
                workspaceBackendUrl,
              );
              const status = await resp.json();
              return { ...wsInfo, status, error: null };
            } catch (err) {
              return {
                ...wsInfo,
                status: null,
                error: err.message || "Failed to fetch status.",
              };
            }
          },
          isActive,
        );
        if (isActive()) setWorkspaces(workspacesStatus);
      }
    } catch (err) {
      if (isActive()) {
        setWorkspacesError(err.message || "Failed to load workspaces.");
      }
    } finally {
      if (isActive()) setWorkspacesLoading(false);
    }
  };

  const refreshWorkspaces = usePolling(fetchWorkspaces, 15000);

  const fetchPodLogs = async (workspaceId, nodeIndex) => {
    setLogDrawerLoading(true);
    setLogDrawerError(null);
    try {
      const podLogUrl = `/workspace/logs/${workspaceId}/${nodeIndex}`;
      const resp = await supabaseAuthenticatedApi.get(
        podLogUrl,
        workspaceBackendUrl,
      );
      const text = await resp.text();
      setLogDrawerContent(text);
    } catch (err) {
      setLogDrawerError(err.message || "Failed to fetch logs");
    } finally {
      setLogDrawerLoading(false);
    }
  };

  const handleActionMenuOpen = (event, workspaceId) =>
    setWorkspaceMenuState({ anchorEl: event.currentTarget, workspaceId });
  const handleActionMenuClose = () =>
    setWorkspaceMenuState({ anchorEl: null, workspaceId: null });

  const handlePodActionMenuOpen = (event, pod) =>
    setPodMenuState({ anchorEl: event.currentTarget, pod });
  const handlePodActionMenuClose = () =>
    setPodMenuState({ anchorEl: null, pod: null });

  const handleToggleExpand = (workspaceId) => {
    setExpandedRows((prev) => ({ ...prev, [workspaceId]: !prev[workspaceId] }));
    handleActionMenuClose();
  };

  const handleOpenTerminateDialog = () => {
    setWorkspaceToDelete(workspaceMenuState.workspaceId);
    setOpenConfirmDialog(true);
    handleActionMenuClose();
  };
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setWorkspaceToDelete(null);
  };

  const handleConfirmTerminate = async () => {
    if (!workspaceToDelete) return;
    setWorkspaceTerminating(true);
    setWorkspaceTerminationError(null);
    handleCloseConfirmDialog();
    try {
      await supabaseAuthenticatedApi.delete(
        `/workspace/delete/${workspaceToDelete}`,
        workspaceBackendUrl,
      );
      await refreshWorkspaces();
    } catch (err) {
      setWorkspaceTerminationError(
        err?.response?.data?.detail ||
          `Failed to terminate workspace ${workspaceToDelete}.`,
      );
    } finally {
      setWorkspaceTerminating(false);
    }
  };

  const handleViewProfiles = () => {
    const id = workspaceMenuState.workspaceId;
    if (id) {
      const workspace = workspaces.find((ws) => ws.workspace_id === id);
      setProfileDrawerState({ open: true, workspace });
    }
    handleActionMenuClose();
  };

  const handleViewLogs = () => {
    if (podMenuState.pod) {
      setLogDrawerState({ open: true, pod: podMenuState.pod });
      fetchPodLogs(podMenuState.pod.workspace_id, podMenuState.pod.node_index);
    }
    handlePodActionMenuClose();
  };

  const handleViewDetails = () => {
    if (podMenuState.pod) {
      const workspace = workspaces.find(
        (ws) => ws.workspace_id === podMenuState.pod.workspace_id,
      );
      const statefulset = workspace?.status?.statefulset;
      setDetailsDrawerState({ open: true, pod: podMenuState.pod, statefulset });
    }
    handlePodActionMenuClose();
  };

  return (
    <>
      {workspaceTerminating && (
        <ProgressMessage>Terminating selected workspace...</ProgressMessage>
      )}

      {workspaceTerminationError && (
        <Alert
          severity="error"
          sx={{ m: 2 }}
          onClose={() => setWorkspaceTerminationError(null)}
        >
          {workspaceTerminationError}
        </Alert>
      )}

      {workspacesError && (
        <Alert
          severity="error"
          sx={{ m: 2 }}
          onClose={() => setWorkspacesError(null)}
        >
          {workspacesError}
        </Alert>
      )}

      <SectionHeader
        icon={TerminalOutlinedIcon}
        title="Workspaces"
        count={workspacesLoading ? undefined : workspaces.length}
        subtitle="Interactive, SSH-accessible environments for development."
        action={
          <Button
            variant="solidButton"
            startIcon={<AddIcon />}
            onClick={onWorkspaceCreation}
          >
            Create a Workspace
          </Button>
        }
      />

      <Box sx={{ width: "100%" }}>
        <WorkspacesListTable
          workspaces={workspaces}
          loading={workspacesLoading}
          expandedRows={expandedRows}
          onToggleExpand={handleToggleExpand}
          onActionMenuOpen={handleActionMenuOpen}
          onPodActionMenuOpen={handlePodActionMenuOpen}
        />
      </Box>

      {/* Workspace-level action menu */}
      <Menu
        id="workspace-action-menu"
        anchorEl={workspaceMenuState.anchorEl}
        open={Boolean(workspaceMenuState.anchorEl)}
        onClose={handleActionMenuClose}
        {...ACTION_MENU_PROPS}
      >
        <MenuItem onClick={handleViewProfiles}>
          <StyledListItemIcon>
            <InfoIcon fontSize="small" />
          </StyledListItemIcon>
          View Profiles
        </MenuItem>
        <MenuItem onClick={handleOpenTerminateDialog} className="destructive">
          <StyledListItemIcon>
            <DeleteIcon fontSize="small" />
          </StyledListItemIcon>
          Terminate
        </MenuItem>
      </Menu>

      {/* Pod inline action menu */}
      <Menu
        id="pod-action-menu"
        anchorEl={podMenuState.anchorEl}
        open={Boolean(podMenuState.anchorEl)}
        onClose={handlePodActionMenuClose}
        {...ACTION_MENU_PROPS}
      >
        <MenuItem onClick={handleViewDetails}>
          <StyledListItemIcon>
            <ArticleIcon fontSize="small" />
          </StyledListItemIcon>
          View Details
        </MenuItem>
        <MenuItem onClick={handleViewLogs}>
          <StyledListItemIcon>
            <ArticleIcon fontSize="small" />
          </StyledListItemIcon>
          View Logs
        </MenuItem>
      </Menu>

      <ConfirmTerminateDialog
        open={openConfirmDialog}
        title="Confirm Terminating Workspace"
        description={
          <>
            Are you sure you want to terminate workspace &quot;
            {workspaces?.find((ws) => ws.workspace_id === workspaceToDelete)
              ?.status?.statefulset?.user_label || workspaceToDelete}
            &quot;? This action cannot be undone.
          </>
        }
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmTerminate}
      />

      {/* Drawer: Profile */}
      <SideDrawer
        open={profileDrawerState.open}
        onClose={() => setProfileDrawerState({ open: false, workspace: null })}
        title="Workspace Profile"
      >
        <ExperimentProfile experiment={profileDrawerState.workspace} />
      </SideDrawer>

      {/* Drawer: Pod Logs */}
      <SideDrawer
        open={logDrawerState.open}
        onClose={() => {
          setLogDrawerState({ open: false, pod: null });
          setLogDrawerContent("");
          setLogDrawerError(null);
          setLogDrawerLoading(false);
        }}
        title={`Pod Logs (${logDrawerState.pod?.name || "..."})`}
      >
        <PodLogsView
          logContent={logDrawerContent}
          logError={logDrawerError}
          logLoading={logDrawerLoading}
        />
      </SideDrawer>

      {/* Drawer: Pod Conditions details */}
      <SideDrawer
        open={detailsDrawerState.open}
        onClose={() =>
          setDetailsDrawerState({ open: false, statefulset: null, pod: null })
        }
        title={`Pod Details (${detailsDrawerState.pod?.name || "..."})`}
      >
        <StatefulSetConditionDetailsView
          statefulset={detailsDrawerState.statefulset}
          pod={detailsDrawerState.pod}
        />
      </SideDrawer>
    </>
  );
};

WorkspacesSection.propTypes = {
  onWorkspaceCreation: PropTypes.func,
};

export default WorkspacesSection;
